import { azureMapsKey } from './map/config';
import type { GeoCentre } from './defaultCentre';

/**
 * Forward geocoding: turning what somebody typed into a place on the map.
 *
 * Kept behind this module rather than called from a component so the network shape stays in one
 * place and the screens can be tested without stubbing `fetch` against a Microsoft URL. It shares
 * the Azure Maps key with the map itself and degrades the same way: no key, no search box.
 */

/** A place the user can pick from search results. */
export interface GeocodeResult extends GeoCentre {
  /** A stable key for list rendering. */
  readonly id: string;
  /** The full address, shown under the short label. */
  readonly detail: string;
}

/** The Azure Maps fuzzy-search endpoint. */
const SEARCH_URL = 'https://atlas.microsoft.com/search/fuzzy/json';

/** Enough to choose from without turning the dropdown into a wall. */
const RESULT_LIMIT = 5;

/** The subset of the Azure Maps response this module reads. */
interface FuzzySearchResponse {
  readonly results?: readonly {
    readonly id?: string;
    readonly position?: { readonly lat?: number; readonly lon?: number };
    readonly address?: {
      readonly freeformAddress?: string;
      readonly municipality?: string;
      readonly countrySubdivision?: string;
    };
    readonly poi?: { readonly name?: string };
  }[];
}

/**
 * Reports whether location search can run at all.
 *
 * @returns `true` when an Azure Maps key is configured.
 */
export function isGeocodingConfigured(): boolean {
  return azureMapsKey() !== '';
}

/**
 * Builds the short label shown as the primary line of a result.
 *
 * Prefers "City, ST" because that is how people think about where events are; falls back to a POI
 * name, then the full address, so a result is never unlabelled.
 *
 * @param result - One Azure Maps result.
 * @returns A short place name.
 */
function shortLabel(result: NonNullable<FuzzySearchResponse['results']>[number]): string {
  const city = result.address?.municipality;
  const region = result.address?.countrySubdivision;

  if (city !== undefined && city !== '') {
    return region !== undefined && region !== '' ? `${city}, ${region}` : city;
  }

  return result.poi?.name ?? result.address?.freeformAddress ?? 'Unknown place';
}

/**
 * Searches for places matching free text.
 *
 * Returns an empty list rather than throwing when search is unconfigured or the request fails:
 * a failed lookup should leave the visitor on the map they already have, not break the screen.
 *
 * @param query - What the user typed.
 * @param signal - Aborts an in-flight request when the query moves on.
 * @returns Matching places, best first, or an empty list.
 */
export async function searchPlaces(
  query: string,
  signal?: AbortSignal,
): Promise<readonly GeocodeResult[]> {
  const key = azureMapsKey();
  const trimmed = query.trim();

  if (key === '' || trimmed === '') {
    return [];
  }

  const url =
    `${SEARCH_URL}?api-version=1.0&subscription-key=${encodeURIComponent(key)}` +
    `&limit=${String(RESULT_LIMIT)}&typeahead=true&query=${encodeURIComponent(trimmed)}`;

  try {
    const response = await fetch(url, { signal });

    if (!response.ok) {
      return [];
    }

    const body = (await response.json()) as FuzzySearchResponse;

    return (body.results ?? [])
      .filter((r) => typeof r.position?.lat === 'number' && typeof r.position.lon === 'number')
      .map((r, index) => ({
        id: r.id ?? `result-${String(index)}`,
        lat: r.position?.lat ?? 0,
        lon: r.position?.lon ?? 0,
        label: shortLabel(r),
        detail: r.address?.freeformAddress ?? '',
      }));
  } catch {
    // Includes the AbortError raised when the query moves on, which is routine, not a failure.
    return [];
  }
}
